import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface ChargingOutputData {
  minkW: number;
  maxkW: number;
  hardwareCost: number;
  installCost: number;
}

export interface ChargingOutputResponse {
  _id: string;
  userId: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
  };
  minkW: number;
  maxkW: number;
  hardwareCost: number;
  installCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChargingOutputApiResponse {
  success: boolean;
  message: string;
  data?: {
    chargingOutput: ChargingOutputResponse;
  };
}

export interface ChargingOutputsApiResponse {
  success: boolean;
  message: string;
  data?: {
    chargingOutputs: ChargingOutputResponse[];
    total: number;
    page: number;
    pages: number;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// SUBMIT CHARGING OUTPUT ACTION
export const createUserChargingOutput = createAsyncThunk<
  ChargingOutputApiResponse,
  ChargingOutputData,
  { rejectValue: string }
>(
  "chargingOutput/createUserChargingOutput",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<ChargingOutputApiResponse>(
        "/charging-output/create",
        formData
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Charging output created successfully"
        );
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to create charging output"
        );
        return rejectWithValue(
          response.data.message || "Failed to create charging output"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ChargingOutputApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET USER-SPECIFIC CHARGING OUTPUTS ACTION
export const getUserSpecificChargingOutput = createAsyncThunk<
  ChargingOutputsApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "chargingOutput/getUserSpecificChargingOutput",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.get<ChargingOutputsApiResponse>(
        `/charging-output?page=${page}&limit=${limit}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to retrieve charging outputs"
        );
        return rejectWithValue(
          response.data.message || "Failed to retrieve charging outputs"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ChargingOutputsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
